import { forwardRef, type ReactNode } from "react";

interface TabButtonProps {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}

/**
 * Pill-style tab button used across parent pages (employees, settings, admin, knowledge-base).
 * Icons and badge counts are passed as children. Does not manage tab state.
 *
 * a11y: role="tab" + aria-selected + roving tabindex (0 when active, -1 otherwise) so arrow-key
 * navigation lands on a single tab per tablist, and forwardRef so a container can programmatically
 * focus a tab. aria-controls is intentionally omitted — this standalone primitive owns no panel id;
 * add it once a tablist container wires each tab to its panel.
 *
 * Out of scope: underline tabs (transcript drawer), rounded-full filter chips (template grid).
 */
export const TabButton = forwardRef<HTMLButtonElement, TabButtonProps>(
  ({ active, onClick, children }, ref) => {
    return (
      <button
        ref={ref}
        type="button"
        role="tab"
        aria-selected={active}
        tabIndex={active ? 0 : -1}
        onClick={onClick}
        className={`flex items-center gap-2 px-4 py-2 text-sm rounded-lg transition ${
          active
            ? "bg-gold/15 text-foreground border border-accent-text/30 shadow-sm"
            : "bg-card/60 border border-transparent text-text-secondary hover:bg-muted"
        }`}
      >
        {children}
      </button>
    );
  }
);
TabButton.displayName = "TabButton";
